import React from 'react';
import styled from 'styled-components';
import { NavLink } from 'react-router-dom';
import Footer from '../components/Footer';

const Container = styled.div`
  max-width: 1100px;
  margin: 0 auto;
  padding: 0 16px;
`;

const HeroSection = styled.section`
  text-align: center;
  padding: 40px 16px;
  background: linear-gradient(135deg, #2d5a27 0%, #1a3518 100%);
  color: white;
  margin: 0 -16px 40px -16px;

  @media (max-width: 768px) {
    padding: 32px 16px;
    margin: 0 -16px 32px -16px;
  }
`;

const HeroTitle = styled.h1`
  font-size: 1.75rem;
  font-weight: 700;
  margin-bottom: 8px;
  letter-spacing: -0.02em;

  @media (max-width: 768px) {
    font-size: 1.5rem;
  }
`;

const HeroSubtitle = styled.p`
  font-size: 0.95rem;
  margin-bottom: 0;
  opacity: 0.9;
  max-width: 500px;
  margin-left: auto;
  margin-right: auto;
`;

const FreeBanner = styled.div`
  background: #e8f5e9;
  border: 2px solid #2d5a27;
  border-radius: 8px;
  padding: 24px;
  text-align: center;
  margin-bottom: 40px;
`;

const FreeTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 700;
  color: #2d5a27;
  margin-bottom: 8px;
`;

const FreeSubtitle = styled.p`
  font-size: 0.9rem;
  color: #333;
  margin: 0;
  line-height: 1.5;
`;

const SectionTitle = styled.h2`
  font-size: 1.25rem;
  text-align: center;
  margin-bottom: 24px;
  color: #333;
  font-weight: 600;

  @media (max-width: 768px) {
    font-size: 1.1rem;
    margin-bottom: 20px;
  }
`;

const FeaturesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 20px;
  margin-bottom: 48px;
`;

const FeatureCard = styled.div`
  background: white;
  border-radius: 8px;
  padding: 24px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  border: 1px solid #eee;
`;

const FeatureIcon = styled.div`
  width: 40px;
  height: 40px;
  background: #2d5a27;
  color: white;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.7rem;
  font-weight: 700;
  margin-bottom: 12px;
`;

const FeatureTitle = styled.h3`
  font-size: 1rem;
  margin-bottom: 8px;
  color: #333;
  font-weight: 600;
`;

const FeatureDescription = styled.p`
  font-size: 0.85rem;
  color: #666;
  line-height: 1.5;
  margin: 0;
`;

const FeatureList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const FeatureItem = styled.li`
  padding: 6px 0;
  color: #333;
  font-size: 0.85rem;
  position: relative;
  padding-left: 20px;

  &:before {
    content: '✓';
    position: absolute;
    left: 0;
    color: #2d5a27;
    font-weight: bold;
  }
`;

const CTASection = styled.div`
  background: #f8f9fa;
  border-radius: 8px;
  padding: 32px;
  text-align: center;
  margin-bottom: 48px;
`;

const CTATitle = styled.h3`
  font-size: 1.25rem;
  margin-bottom: 12px;
  color: #333;
`;

const CTADescription = styled.p`
  font-size: 0.9rem;
  color: #666;
  margin-bottom: 20px;
  max-width: 500px;
  margin-left: auto;
  margin-right: auto;
`;

const CTAButton = styled(NavLink)`
  display: inline-block;
  background: #2d5a27;
  color: white;
  padding: 12px 32px;
  border-radius: 6px;
  text-decoration: none;
  font-weight: 600;
  font-size: 0.95rem;
  transition: all 0.2s ease;

  &:hover {
    background: #1f3d1b;
    transform: translateY(-1px);
  }
`;

const ComparisonSection = styled.section`
  margin-bottom: 48px;
`;

const ComparisonTable = styled.div`
  background: white;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  border: 1px solid #eee;
`;

const TableHeader = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  background: #f8f9fa;
  font-weight: 600;
  font-size: 0.85rem;
  color: #333;

  @media (max-width: 768px) {
    display: none;
  }
`;

const TableRow = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  border-bottom: 1px solid #eee;

  &:last-child {
    border-bottom: none;
  }

  @media (max-width: 768px) {
    display: block;
    padding: 12px 16px;
  }
`;

const TableCell = styled.div`
  padding: 12px 16px;
  display: flex;
  align-items: center;
  font-size: 0.85rem;

  @media (max-width: 768px) {
    padding: 4px 0;
    &:before {
      content: attr(data-label);
      font-weight: 600;
      width: 100px;
      display: inline-block;
    }
  }
`;

const Pricing = ({ user }) => {
  return (
    <>
      <Container>
        <HeroSection>
          <HeroTitle>100% Free for Property Owners & Managers</HeroTitle>
          <HeroSubtitle>
            Professional showing management and compliance tracking at no cost
          </HeroSubtitle>
        </HeroSection>

        <FreeBanner>
          <FreeTitle>ShowingBooker is Completely Free</FreeTitle>
          <FreeSubtitle>
            We believe every property owner and manager deserves access to professional compliance tools.
            No credit card required. No hidden fees. No limits.
          </FreeSubtitle>
        </FreeBanner>

        <SectionTitle>Everything Included...Free Forever</SectionTitle>

        <FeaturesGrid>
          <FeatureCard>
            <FeatureIcon>UP</FeatureIcon>
            <FeatureTitle>Unlimited Properties</FeatureTitle>
            <FeatureDescription>
              Add and manage as many properties as you need. No artificial limits or tier restrictions.
            </FeatureDescription>
          </FeatureCard>

          <FeatureCard>
            <FeatureIcon>LC</FeatureIcon>
            <FeatureTitle>Legal Compliance</FeatureTitle>
            <FeatureDescription>
              Automated 24-hour notices compliant with California Civil Code Section 1954.
            </FeatureDescription>
          </FeatureCard>

          <FeatureCard>
            <FeatureIcon>MC</FeatureIcon>
            <FeatureTitle>Multi-Channel Delivery</FeatureTitle>
            <FeatureDescription>
              Send notices via SMS, email, and certified mail...all tracked and documented.
            </FeatureDescription>
          </FeatureCard>

          <FeatureCard>
            <FeatureIcon>PDF</FeatureIcon>
            <FeatureTitle>PDF Reports</FeatureTitle>
            <FeatureDescription>
              Generate professional compliance reports with complete audit trails.
            </FeatureDescription>
          </FeatureCard>

          <FeatureCard>
            <FeatureIcon>CT</FeatureIcon>
            <FeatureTitle>Custom Templates</FeatureTitle>
            <FeatureDescription>
              Create and save notice templates customized to your needs.
            </FeatureDescription>
          </FeatureCard>

          <FeatureCard>
            <FeatureIcon>SP</FeatureIcon>
            <FeatureTitle>Secure & Private</FeatureTitle>
            <FeatureDescription>
              Enterprise-grade security. Your data is encrypted and never shared.
            </FeatureDescription>
          </FeatureCard>
        </FeaturesGrid>

        <ComparisonSection>
          <SectionTitle>All Features Included</SectionTitle>
          <ComparisonTable>
            <TableHeader>
              <TableCell>Feature</TableCell>
              <TableCell>Included</TableCell>
            </TableHeader>
            <TableRow>
              <TableCell data-label="Feature:">Unlimited properties</TableCell>
              <TableCell data-label="Included:">✓ Free</TableCell>
            </TableRow>
            <TableRow>
              <TableCell data-label="Feature:">24-hour notice automation</TableCell>
              <TableCell data-label="Included:">✓ Free</TableCell>
            </TableRow>
            <TableRow>
              <TableCell data-label="Feature:">SMS + Email delivery</TableCell>
              <TableCell data-label="Included:">✓ Free</TableCell>
            </TableRow>
            <TableRow>
              <TableCell data-label="Feature:">Certified mail integration</TableCell>
              <TableCell data-label="Included:">✓ Free</TableCell>
            </TableRow>
            <TableRow>
              <TableCell data-label="Feature:">Compliance logging & audit trails</TableCell>
              <TableCell data-label="Included:">✓ Free</TableCell>
            </TableRow>
            <TableRow>
              <TableCell data-label="Feature:">PDF report generation</TableCell>
              <TableCell data-label="Included:">✓ Free</TableCell>
            </TableRow>
            <TableRow>
              <TableCell data-label="Feature:">Custom notice templates</TableCell>
              <TableCell data-label="Included:">✓ Free</TableCell>
            </TableRow>
            <TableRow>
              <TableCell data-label="Feature:">Proof of delivery tracking</TableCell>
              <TableCell data-label="Included:">✓ Free</TableCell>
            </TableRow>
            <TableRow>
              <TableCell data-label="Feature:">Email support</TableCell>
              <TableCell data-label="Included:">✓ Free</TableCell>
            </TableRow>
          </ComparisonTable>
        </ComparisonSection>

        <CTASection>
          <CTATitle>Ready to Simplify Your Compliance?</CTATitle>
          <CTADescription>
            Join property owners and managers who trust ShowingBooker for their tenant notification needs.
          </CTADescription>
          <CTAButton to="/register">Get Started Free</CTAButton>
        </CTASection>
      </Container>

      <Footer />
    </>
  );
};

export default Pricing;
